import { InnerBox } from "@/components/elements/InnerBox";
import { JobListTop } from "@/features/job/components/job-list/JobListTop";
import { usePagination } from "@/hooks/usePagination";
import { divider, secondary } from "@/theme/colors";
import { Box, Menu, MenuItem as MuiMenuItem, Stack, Typography } from "@mui/material";
import { FC, useState, useCallback, useEffect, useRef, useMemo, MouseEvent as ReactMouseEvent } from "react";
import { DEFAULT_PER_PAGE, START_PAGE } from "@/constants/pagination";
import { TGetJobListReqDto } from "@/api/services/main/job/dtos/get-job-list.dto";
import { checkDeletableJobsApi, deleteJobsApi, useJobList, getJobListApi } from "@/api/services/main/job";
import { Pagination } from "@/components/elements/Pagination";
import { JobListTable } from "@/features/job/components/job-list/JobListTable";
import {
  JOB_DISPLAY_COLUMNS_STORAGE_KEY,
  JOB_ADVANCED_FILTERS_STORAGE_KEY,
  JOB_APPLIED_CONDITION_NAME_STORAGE_KEY,
  JOB_APPLIED_CONDITION_ID_STORAGE_KEY,
  JOB_SORT_STORAGE_KEY,
  JOB_BULK_DELETE_CONFIRM_MESSAGE,
  JOB_SELECTED_STORAGE_KEY,
  JOB_SELECT_ALL_STORAGE_KEY,
  JOB_DEFAULT_SEEDED_STORAGE_KEY,
} from "@/features/job/constants/common";
import { useSavedConditionList } from "@/api/services/main/saved-condition";
import { enforceLockedColumns, resolveSavedConditionColumns } from "@/components/commons/filter-condition/filter-condition-adapter-kit";
import { clearSessionFilterState, dropLegacyLocalFilterState, readSessionFilterState, writeSessionFilterState } from "@/utils/session-filter-state";
import { useSanitizedSortParams } from "@/hooks/useSanitizedSortParams";
import { useSanitizedPageParam } from "@/hooks/useSanitizedPageParam";
import { JOB_SORTABLE_FIELDS } from "@/constants/sortable-fields";
import { AppRoutes } from "@/routes/config";
import { getDynamicRoute } from "@/utils/url";
import { Button } from "@/components/elements/Button";
import { IconActionDelete, IconChevronDown, IconPlus } from "@/assets/icons";
import { ScanImageJobModal } from "@/features/job/components/job-scan-image/ScanImageJobModal";
import { ImportJobModal } from "@/features/job/components/job-import/ImportJobModal";
import { useVisible } from "@/hooks/useVisible";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { useDialog } from "@/hooks/useDialog";
import { toast } from "@/utils/toast";
import { isApiError, showError } from "@/utils/error";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { queryClient } from "@/constants/queryClient";
import { TJobFilterFormExtended, TJobDisplayColumnSetting } from "@/features/job/components/job-list/job-filter-shared";
import {
  stripEmptyJobFilterFields,
  normalizeJobDateFields,
  normalizeJobMultiSelectFields,
  isEmptyJobFilterValue,
  defaultJobFilterValues,
  defaultJobDisplayColumns,
  JOB_FILTER_COMPANY_ARCHIVED,
  JOB_LOCKED_COLUMN_IDS,
} from "@/features/job/components/job-list/job-filter-shared";
import dayjs from "dayjs";

type TJobListPageProps = {};

const shouldPreserveSelection = (locationState: unknown): boolean => {
  if (!locationState || typeof locationState !== "object") return false;
  const state = locationState as { preserveSelection?: boolean };
  return state.preserveSelection === true;
};

export const JobListPage: FC<TJobListPageProps> = () => {
  const location = useLocation();

  const selectionInitializedRef = useRef(false);
  if (!selectionInitializedRef.current) {
    selectionInitializedRef.current = true;
    if (!shouldPreserveSelection(location.state)) {
      sessionStorage.removeItem(JOB_SELECTED_STORAGE_KEY);
      sessionStorage.removeItem(JOB_SELECT_ALL_STORAGE_KEY);
    }
  }
  const pagReq = usePagination();
  const [searchParams, setSearchParams] = useSearchParams();
  const sortInitializedRef = useRef(false);

  useSanitizedPageParam({ basePath: AppRoutes.jobList });
  const { sortValue } = useSanitizedSortParams({
    allowedFields: [...JOB_SORTABLE_FIELDS],
  });

  // Restore sort from sessionStorage on mount if URL has no sorts param
  useEffect(() => {
    if (sortInitializedRef.current) return;
    sortInitializedRef.current = true;

    if (!searchParams.get("sorts")) {
      const storedSort = sessionStorage.getItem(JOB_SORT_STORAGE_KEY);
      if (storedSort) {
        const params = new URLSearchParams(searchParams);
        params.set("sorts", storedSort);
        setSearchParams(params, { replace: true });
      }
    }
  }, []);

  useEffect(() => {
    if (sortValue) {
      sessionStorage.setItem(JOB_SORT_STORAGE_KEY, sortValue);
    } else {
      sessionStorage.removeItem(JOB_SORT_STORAGE_KEY);
    }
  }, [sortValue]);

  const scanImageJobModal = useVisible();
  const importJobModal = useVisible();
  const confirmDelete = useDialog();
  const undeletableDialog = useDialog();
  

  // handle case reload page
  useEffect(() => {
    const navEntries = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
    const isReload = navEntries.length > 0 && navEntries[0].type === "reload";

    if (isReload) {
      sessionStorage.removeItem(JOB_SELECTED_STORAGE_KEY);
      sessionStorage.removeItem(JOB_SELECT_ALL_STORAGE_KEY);
    }
  }, []);

  const [isSelectAll, setIsSelectAll] = useState<boolean>(() => {
    return sessionStorage.getItem(JOB_SELECT_ALL_STORAGE_KEY) === "true";
  });
  const [selectedJobIds, setSelectedJobIds] = useState<number[]>(() => {
    const raw = sessionStorage.getItem(JOB_SELECTED_STORAGE_KEY);
    if (!raw) return [];
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      return [];
    } catch {
      return [];
    }
  });
  const [undeletableJobs, setUndeletableJobs] = useState<{ id: number; name: string }[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const [selectionUpdateTrigger, setSelectionUpdateTrigger] = useState(0);
  // Accumulate job name mapping across pages so we can show names for jobs selected on other pages
  const jobNameMapRef = useRef<Record<number, string>>({});

  const handleSelectionChange = useCallback((selectedIds: number[], isAllSelected: boolean) => {
    setIsSelectAll(isAllSelected);
    setSelectedJobIds(selectedIds);
    setSelectionUpdateTrigger((prev) => prev + 1);
  }, []);

  const getJobSelected = useCallback(() => selectedJobIds, [selectedJobIds]);

  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const isMoreMenuOpen = Boolean(moreAnchorEl);

  // The applied condition follows the filters: kept across in-app navigation, dropped on
  // reload so the default condition can claim the list again.
  const [appliedConditionName, setAppliedConditionName] = useState<string>(
    () => readSessionFilterState<string>(JOB_APPLIED_CONDITION_NAME_STORAGE_KEY) ?? "",
  );
  const [appliedConditionId, setAppliedConditionId] = useState<number | null>(() =>
    readSessionFilterState<number>(JOB_APPLIED_CONDITION_ID_STORAGE_KEY),
  );

  const handleAppliedConditionNameChange = useCallback((name: string) => {
    setAppliedConditionName(name);
    if (name) {
      writeSessionFilterState(JOB_APPLIED_CONDITION_NAME_STORAGE_KEY, name);
    } else {
      clearSessionFilterState(JOB_APPLIED_CONDITION_NAME_STORAGE_KEY);
    }
  }, []);

  const handleAppliedConditionIdChange = useCallback((id: number | null) => {
    setAppliedConditionId(id);
    if (id !== null) {
      writeSessionFilterState(JOB_APPLIED_CONDITION_ID_STORAGE_KEY, id);
    } else {
      clearSessionFilterState(JOB_APPLIED_CONDITION_ID_STORAGE_KEY);
    }
  }, []);

  // Kept for the lifetime of the document only — a reload falls back to the default
  // saved condition, or to no filter when the user has none.
  const [advancedFilters, setAdvancedFilters] = useState<TJobFilterFormExtended | null>(() => {
    const saved = readSessionFilterState<Partial<TJobFilterFormExtended>>(JOB_ADVANCED_FILTERS_STORAGE_KEY);
    if (!saved) return null;
    return {
      ...defaultJobFilterValues,
      ...normalizeJobMultiSelectFields(normalizeJobDateFields(saved) as Partial<Record<string, unknown>>),
    } as TJobFilterFormExtended;
  });

  // These keys were persisted to localStorage before the filter became session-scoped.
  useEffect(() => {
    dropLegacyLocalFilterState([
      JOB_ADVANCED_FILTERS_STORAGE_KEY,
      JOB_APPLIED_CONDITION_NAME_STORAGE_KEY,
      JOB_APPLIED_CONDITION_ID_STORAGE_KEY,
    ]);
  }, []);

  // On a fresh document the session holds nothing, so the user's default condition seeds
  // the list. Applying it only once keeps a later "clear" from being undone on the next
  // render, and the ref survives the remount that returning from a detail page causes.
  const { data: savedConditionsData } = useSavedConditionList("job");
  const hasSeededDefault = useRef(readSessionFilterState<boolean>(JOB_DEFAULT_SEEDED_STORAGE_KEY) ?? false);

  useEffect(() => {
    if (hasSeededDefault.current || !savedConditionsData) return;
    hasSeededDefault.current = true;
    writeSessionFilterState(JOB_DEFAULT_SEEDED_STORAGE_KEY, true);

    const fallback = savedConditionsData.data.find((item) => item.isDefault);
    if (!fallback) return;

    const filters = {
      ...defaultJobFilterValues,
      ...normalizeJobMultiSelectFields(normalizeJobDateFields(fallback.filters ?? {}) as Partial<Record<string, unknown>>),
    } as TJobFilterFormExtended;
    setAdvancedFilters(filters);
    writeSessionFilterState(JOB_ADVANCED_FILTERS_STORAGE_KEY, stripEmptyJobFilterFields(filters));
    setAppliedConditionName(fallback.title);
    writeSessionFilterState(JOB_APPLIED_CONDITION_NAME_STORAGE_KEY, fallback.title);
    setAppliedConditionId(Number(fallback.id));
    writeSessionFilterState(JOB_APPLIED_CONDITION_ID_STORAGE_KEY, Number(fallback.id));
    const seededColumns = resolveSavedConditionColumns(fallback, defaultJobDisplayColumns);
    setDisplayColumnSettings(seededColumns);
    localStorage.setItem(JOB_DISPLAY_COLUMNS_STORAGE_KEY, JSON.stringify(seededColumns));
  }, [savedConditionsData]);

  const resetToFirstPage = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(START_PAGE));
      return next;
    });
  }, [setSearchParams]);

  const handleApplyAdvancedFilters = useCallback(
    (filters: TJobFilterFormExtended) => {
      const hasAnyFilter = Object.values(filters).some((v) => !isEmptyJobFilterValue(v));
      if (hasAnyFilter) {
        setAdvancedFilters(filters);
        writeSessionFilterState(JOB_ADVANCED_FILTERS_STORAGE_KEY, stripEmptyJobFilterFields(filters));
      } else {
        setAdvancedFilters(null);
        clearSessionFilterState(JOB_ADVANCED_FILTERS_STORAGE_KEY);
      }
      resetToFirstPage();
    },
    [resetToFirstPage],
  );

  const [displayColumnSettings, setDisplayColumnSettings] = useState<TJobDisplayColumnSetting[] | null>(() => {
    const saved = localStorage.getItem(JOB_DISPLAY_COLUMNS_STORAGE_KEY);
    if (!saved) return null;
    try {
      const parsed: TJobDisplayColumnSetting[] = JSON.parse(saved);
      // Sync label-only with latest defaults: keep stored visible/order, refresh label by id
      let hasLabelChange = false;
      const synced = parsed.map((col) => {
        const def = defaultJobDisplayColumns.find((d) => d.id === col.id);
        if (def && def.label !== col.label) {
          hasLabelChange = true;
          return { ...col, label: def.label };
        }
        return col;
      });
      if (hasLabelChange) {
        localStorage.setItem(JOB_DISPLAY_COLUMNS_STORAGE_KEY, JSON.stringify(synced));
      }
      return synced;
    } catch {
      return null;
    }
  });
  const lockedDisplayColumnSettings = useMemo(
    () => displayColumnSettings && enforceLockedColumns(displayColumnSettings, JOB_LOCKED_COLUMN_IDS),
    [displayColumnSettings],
  );

  const handleApplyDisplaySettings = useCallback((columns: TJobDisplayColumnSetting[]) => {
    setDisplayColumnSettings(columns);
  }, []);

  const query: TGetJobListReqDto = {
    page: pagReq.page || START_PAGE,
    perPage: pagReq.perPage || DEFAULT_PER_PAGE,
    ...(advancedFilters && {
      // status array: extract company_archived as special flag, pass real statuses separately
      ...(advancedFilters.status.includes(JOB_FILTER_COMPANY_ARCHIVED) ? { isCompanyArchive: "true" } : {}),
      ...(() => {
        const realStatuses = advancedFilters.status.filter((s) => s !== JOB_FILTER_COMPANY_ARCHIVED);
        return realStatuses.length > 0 ? { status: realStatuses } : {};
      })(),
      ...(advancedFilters.tagIds.length > 0 ? { tagIds: advancedFilters.tagIds } : {}),
      ...(advancedFilters.managementNumber ? { managementNumber: advancedFilters.managementNumber } : {}),
      ...(advancedFilters.companyName.length > 0 ? { companyName: advancedFilters.companyName } : {}),
      ...(advancedFilters.jobName ? { jobName: advancedFilters.jobName } : {}),
      ...(advancedFilters.recruiterId.length > 0 ? { recruiterId: advancedFilters.recruiterId } : {}),
      ...(advancedFilters.companySpecificIndustryId.length > 0 ? { companySpecificIndustryId: advancedFilters.companySpecificIndustryId } : {}),
      ...(advancedFilters.japaneseLevelType.length > 0 ? { japaneseLevelType: advancedFilters.japaneseLevelType } : {}),
      ...(advancedFilters.recruitmentNumber ? { recruitmentNumber: Number(advancedFilters.recruitmentNumber) } : {}),
      ...(advancedFilters.workplace ? { workplace: advancedFilters.workplace } : {}),
      ...(advancedFilters.recruitmentLocationType.length > 0 ? { recruitmentLocationType: advancedFilters.recruitmentLocationType } : {}),
      ...(advancedFilters.registrationDateFrom ? { registrationDateFrom: dayjs(advancedFilters.registrationDateFrom).format("YYYY-MM-DD") } : {}),
      ...(advancedFilters.registrationDateTo ? { registrationDateTo: dayjs(advancedFilters.registrationDateTo).format("YYYY-MM-DD") } : {}),
    }),
  };

  if (sortValue) {
    query.sorts = [sortValue as `${string}|${string}`];
  }

  const { data, isLoading } = useJobList(query);

  // Update job name map whenever rows change
  useEffect(() => {
    (data?.data ?? []).forEach((row) => {
      jobNameMapRef.current[row.id] = row.jobName || row.managementNumber || String(row.id);
    });
  }, [data?.data]);

  const selectedCount = useMemo(() => {
    if (isSelectAll) {
      const total = data?.pagination?.total ?? 0;
      return Math.max(0, total - selectedJobIds.length);
    }
    return selectedJobIds.length;
  }, [isSelectAll, selectedJobIds, data?.pagination?.total, selectionUpdateTrigger]);

  const handleDelete = async () => {
    let deleteIds: number[] = [];
    if (isSelectAll) {
      const totalCount = data?.pagination?.total ?? 0;
      if (totalCount === 0) return;
      try {
        setIsDeleting(true);
        // Fetch all matching job IDs
        const allJobsRes = await getJobListApi({
          ...query,
          page: 1,
          perPage: totalCount,
        });
        const allJobs = allJobsRes.data ?? [];
        deleteIds = allJobs.map((j) => j.id).filter((id) => !selectedJobIds.includes(id));
      } catch (error) {
        showError(error);
        setIsDeleting(false);
        return;
      }
    } else {
      deleteIds = selectedJobIds;
    }

    if (deleteIds.length === 0 || isDeleting) return;

    try {
      setIsDeleting(true);
      const { data: deletableMap } = await checkDeletableJobsApi(deleteIds);
      const undeletableIds = deleteIds.filter((id) => !deletableMap[id]);

      if (undeletableIds.length > 0) {
        const names = undeletableIds.map((id) => ({ id, name: jobNameMapRef.current[id] || String(id) }));
        setUndeletableJobs(names);
        setIsDeleting(false);
        await undeletableDialog.show();
        return;
      }

      setIsDeleting(false);
      const confirmed = await confirmDelete.show();
      if (!confirmed) return;

      setIsDeleting(true);
      const rs = await deleteJobsApi(deleteIds);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      setSelectedJobIds([]);
      setIsSelectAll(false);
      sessionStorage.removeItem(JOB_SELECTED_STORAGE_KEY);
      sessionStorage.removeItem(JOB_SELECT_ALL_STORAGE_KEY);
      setSelectionResetKey((k) => k + 1);
      setSelectionUpdateTrigger((prev) => prev + 1);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOB.FETCH_LIST] });
    } catch (error: unknown) {
      if (isApiError(error)) {
        toast.error(error.message);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Stack minHeight="100%">
      <Stack mb={3} justifyContent="space-between" alignItems="center" direction="row" width="100%">
        <Typography variant="head20Bold">求人管理</Typography>
        <Box display="flex" gap={2}>
          <Box>
            <Button
              variant="outline"
              size="sm"
              endIcon={
                <IconChevronDown
                  style={{
                    transition: "transform 0.2s",
                    transform: isMoreMenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                />
              }
              onClick={(e: ReactMouseEvent<HTMLButtonElement>) => setMoreAnchorEl(e.currentTarget)}
            >
              <Typography variant="body14Semi">AI取り込み / インポート</Typography>
            </Button>
            <Menu
              anchorEl={moreAnchorEl}
              open={isMoreMenuOpen}
              onClose={() => setMoreAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{
                paper: {
                  sx: {
                    mt: 0.5,
                    borderRadius: "8px",
                    boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
                    minWidth: "220px",
                  },
                },
              }}
              sx={{
                ".MuiPaper-root": {
                  padding: 0,
                },
                ".MuiMenu-list": {
                  padding: 0,
                },
              }}
            >
              <MuiMenuItem
                onClick={() => {
                  setMoreAnchorEl(null);
                  scanImageJobModal.open();
                }}
                sx={{ py: 1.5 }}
              >
                <Typography variant="body14">求人票からAI取込</Typography>
              </MuiMenuItem>
              <MuiMenuItem
                onClick={() => {
                  setMoreAnchorEl(null);
                  importJobModal.open();
                }}
                sx={{ py: 1.5 }}
              >
                <Typography variant="body14">一括インポート</Typography>
              </MuiMenuItem>
            </Menu>
          </Box>
          <Button component={Link} to={AppRoutes.jobCreate} size="sm" startIcon={<IconPlus />}>
            <Typography variant="body14Semi">新規求人登録</Typography>
          </Button>
        </Box>
      </Stack>

      <InnerBox padding={3} showDivider={false} bordered={true} borderColor={divider.high}>
        <Stack>
          <JobListTop
            pagination={data?.pagination}
            onApplyAdvancedFilters={handleApplyAdvancedFilters}
            onApplyDisplaySettings={handleApplyDisplaySettings}
            appliedConditionName={appliedConditionName}
            appliedConditionId={appliedConditionId}
            onAppliedConditionNameChange={handleAppliedConditionNameChange}
            onAppliedConditionIdChange={handleAppliedConditionIdChange}
            currentDisplayColumns={lockedDisplayColumnSettings}
            currentAdvancedFilters={advancedFilters}
            selectionProps={{
              selectedCount,
              query,
              getJobSelected,
              isSelectAll,
              excludedIds: selectedJobIds,
            }}
          />
          <Stack spacing={3}>
            <JobListTable
              rows={data?.data ?? []}
              isLoading={isLoading}
              displayColumnSettings={lockedDisplayColumnSettings}
              onSelectionChange={handleSelectionChange}
              selectionResetKey={selectionResetKey}
            />
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Pagination totalPage={data?.pagination?.totalPage ?? 1} />
              <Button
                variant="danger"
                size="sm"
                color="error"
                disabled={isDeleting || selectedCount === 0}
                onClick={handleDelete}
                sx={{ width: "88px", mr: 4 }}
                startIcon={<IconActionDelete />}
              >
                <Typography variant="body14Semi">削除</Typography>
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </InnerBox>
      <ConfirmDeleteModal
        open={confirmDelete.open}
        onOk={confirmDelete.confirm}
        onClose={confirmDelete.cancel}
        message={JOB_BULK_DELETE_CONFIRM_MESSAGE(selectedCount)}
      />
      <ConfirmDeleteModal
        open={undeletableDialog.open}
        onOk={undeletableDialog.cancel}
        onClose={undeletableDialog.cancel}
        message={
          <>
            以下の求人は案件に人材が紐づけられており削除できません。案件を修正の上、改めて削除してください。
            {undeletableJobs.map(({ id, name }) => (
              <div key={id}>
                ・
                <Link
                  to={getDynamicRoute(AppRoutes.jobDetail, { jobId: id })}
                  style={{ color: secondary[80] }}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {name}
                </Link>
              </div>
            ))}
          </>
        }
        hiddenOkBtn
        labelClose="閉じる"
        justifyBtnAction="center"
      />
      <ScanImageJobModal modal={scanImageJobModal} />
      <ImportJobModal modal={importJobModal} />
    </Stack>
  );
};
